import React, { useEffect, useState } from 'react';
import useWireTransferHistory from '../../../../hooks/invoice/useWireTransferHistory';
import { getWireStatusBadgeClass } from './wireBadges';
import moment from 'moment-timezone';
import { ClipLoader } from 'react-spinners';
import { Modal } from '@mui/material';
import WireTransferInvoice from '../WireTransferInvoice';

const WireTranferList = () => {
  const { wireTransfers, loading, error, fetchWireTransfers } = useWireTransferHistory();
  const [sortOrder, setSortOrder] = useState("latest");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedRefId, setSelectedRefId] = useState(null);
  const [openWireTransferInvoiceModal, setOpenWireTransferInvoiceModal] = useState(false);

  useEffect(() => {
    fetchWireTransfers();
  }, []);

  const handleSort = (order) => {
    setSortOrder(order);
    setIsDropdownOpen(false);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setIsStatusDropdownOpen(false);
  };

  const openImagePreview = (imageUrl) => {
    setPreviewImage(imageUrl);
    setIsPreviewOpen(true);
  };

  const openInvoiceModalWithRef = (refId) => {
    setSelectedRefId(refId);
    setOpenWireTransferInvoiceModal(true);
  };

  const filteredAndSortedTransfers = wireTransfers?.data
    ? [...wireTransfers.data]
        .filter((transfer) =>
          statusFilter === 'all' || transfer.status?.toLowerCase() === statusFilter.toLowerCase()
        )
        .sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
        })
    : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ClipLoader size={50} color="#D0021B" />
        <p className="ml-3 text-lg text-gray-500">Loading wire transfers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500 text-center">
          <p>Error loading wire transfers: {error}</p>
          <button
            onClick={fetchWireTransfers}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!wireTransfers?.data?.length) {
    return (
      <div className="flex justify-center items-center min-h-screen text-gray-500">
        No wire transfer records found
      </div>
    );
  }

  return (
    <>
      <div className="w-[90vw] lg:w-[85vw] mx-auto my-6">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Wire Transfer History</h1>
          <div className="flex gap-3 mt-4 sm:mt-0">
            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-100"
              >
                Sort by {sortOrder === "latest" ? "Latest" : "Oldest"}
              </button>
              {isDropdownOpen && (
                <div className="absolute mt-2 w-32 bg-white border shadow-md rounded-md z-10">
                  <button onClick={() => handleSort("latest")} className="block w-full px-4 py-2 text-left hover:bg-gray-100">Latest</button>
                  <button onClick={() => handleSort("oldest")} className="block w-full px-4 py-2 text-left hover:bg-gray-100">Oldest</button>
                </div>
              )}
            </div>

            {/* Status Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-100"
              >
                Filter by {statusFilter === "all" ? "All Status" : statusFilter}
              </button>
              {isStatusDropdownOpen && (
                <div className="absolute mt-2 w-32 bg-white border shadow-md rounded-md z-10">
                  {["all", "pending", "approved", "rejected"].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusFilter(status)}
                      className="block w-full px-4 py-2 text-left hover:bg-gray-100 capitalize"
                    >
                      {status}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200">
          <table className="min-w-full bg-white divide-y divide-gray-200 text-sm text-left">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-6 py-3">Reference ID</th>
                <th className="px-6 py-3">Amount (BSD)</th>
                <th className="px-6 py-3">Uploaded Date</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Payment Proof</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAndSortedTransfers.map((transfer, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{transfer.referenceId || "N/A"}</td>
                  <td className="px-6 py-4">{transfer.depositAmount ? `$${transfer.depositAmount}` : "N/A"}</td>
                  <td className="px-6 py-4">{moment(transfer.createdAt).format("DD-MM-YYYY HH:mm")}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getWireStatusBadgeClass(transfer.status)}`}>
                      {transfer.status || "Pending"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {!transfer.paymentProof && !transfer.depositAmount ? (
                      <button
                        onClick={() => openInvoiceModalWithRef(transfer.referenceId)}
                        className="text-blue-600 hover:underline"
                      >
                        Provide Proof
                      </button>
                    ) : transfer.paymentProof ? (
                      <button
                        onClick={() => openImagePreview(transfer.paymentProof)}
                        className="text-blue-600 hover:underline"
                      >
                        View
                      </button>
                    ) : (
                      "No proof uploaded"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Image Preview Modal */}
      <Modal open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 rounded-lg shadow-xl max-w-3xl w-[90%] max-h-[80vh] overflow-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Payment Proof</h2>
            <button onClick={() => setIsPreviewOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>
          <div className="flex justify-center">
            <img src={previewImage} alt="Payment Proof" className="max-w-full max-h-[60vh] object-contain" />
          </div>
        </div>
      </Modal>

      {/* Provide Proof Modal */}
      <WireTransferInvoice
        isOpen={openWireTransferInvoiceModal}
        onClose={() => setOpenWireTransferInvoiceModal(false)}
        fetchFunds={fetchWireTransfers}
        existingRefId={selectedRefId}
      />
    </>
  );
};

export default WireTranferList;

